<template>
    <v-container fluid class="pa-0">
        <v-row dense>
            <v-col cols="12">

                <v-alert class="body-2 mb-2" prominent icon="mdi-alert"
                         v-show="!selectedSubPanels || selectedSubPanels.length === 0" type="warning">
                    Segment has not yet been selected. Displaying all sub panels and languages!
                </v-alert>

                <title-card
                    no-padding-top
                    :loading="loading"
                    help="email-editor"
                    title="Email editor">

                    <template v-slot:buttons>

                        <v-btn v-show="showRememberButton"
                               color="primary"
                               small
                               @click="rememberLocaleButtonClicked"
                        >
                            Remember locale?
                        </v-btn>

                        <v-btn color="secondary"
                               class="ml-2"
                               small
                               :loading="sending"
                               @click="sendTestEmailToCurrentUser"
                        >
                            Email to me
                        </v-btn>
                    </template>

                    <v-container fluid class="pa-0 pt-2">
                        <v-row dense>

                            <v-col cols=8>

                                <v-select
                                    v-model="subPanelModel"
                                    :items="availableSubPanels"
                                    item-text="name"
                                    item-value="type"
                                    return-object
                                    label="Preview invitation for"
                                    hide-details
                                    @change="subPanelChanged"
                                ></v-select>
                            </v-col>

                            <v-col cols=4 v-if="subPanelModel">

                                <v-select
                                    v-model="localeModel"
                                    :items="locales"
                                    item-text="code"
                                    item-value="code"
                                    hide-details
                                    return-object
                                    label="Preview for locale"
                                    @change="localeChanged"
                                ></v-select>
                            </v-col>

                        </v-row>

                        <!-- SUBJECT AND SENDER NAME -->
                        <v-row dense class="pt-2">
                            <v-col cols="8">
                                <v-text-field
                                    v-if="!loading && subPanelTranslations"
                                    v-model="translations.subject"
                                    label="Email subject"
                                    hide-details
                                    @change="emailSubjectChanged"
                                    persistent-hint>
                                </v-text-field>
                            </v-col>
                            <v-col cols="4">
                                <v-text-field
                                    v-if="!loading && subPanelTranslations"
                                    v-model="translations.sender_name"
                                    hide-details
                                    label="Email sender name"
                                    @change="senderNameChanged"
                                    persistent-hint>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-scale-transition>
                            <!-- EMAIL THEME -->
                            <email-theme v-if="!loading && subPanelTranslations"
                                         :key="template"
                                         :type="template"
                                         :sub-panel="subPanelModel"
                                         :locale="localeModel"
                                         :email-translations="translations"
                                         :sub-panel-translations="subPanelTranslations"
                                         :settings="subPanelModel.email_settings"
                                         class="email mt-3"
                                         @email:translations="updateEmailTranslations"
                                         :edit-email-only="true"
                                         :project="project"
                            ></email-theme>
                        </v-scale-transition>
                    </v-container>

                    <json v-if="false" :data="data"/>

                </title-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>

import EmailTheme from "../../../../components/UI/EmailTheme/index";
import Json from "vue-json-pretty";
import TitleCard from "../../../../components/UI/TitleCard";
import {mapActions, mapState} from "vuex";
import {preferredLocale} from "../../../../components/mixins/preferredLocaleMixin";
import merge from 'deepmerge';

export default {

    mixins: [preferredLocale],

    components: {
        EmailTheme,
        Json,
        TitleCard,
    },

    props: {
        project: {
            type: Object,
            required: true,
        },
        selectedSubPanels: {
            type: Array,
            required: true,
        },
        template: {
            type: String,
            required: true,
        }
    },

    data() {
        return {
            data: null,
            loading: false,
            sending: false,
            selectedLocale: null,
            selectedSubPanel: null,
            subPanelTranslationsLibrary: null,
            showRememberButton: false,
        }
    },

    computed: {

        ...mapState('SubPanels', [
            'subPanels',
            'landingPageTemplates'
        ]),

        localeModel: {
            get() {
                if (this.selectedLocale) {
                    return this.selectedLocale;
                }
            },
            set(code) {
                this.selectedLocale = code;
            }
        },

        subPanelModel: {
            get() {
                if (this.selectedSubPanel) {
                    return this.selectedSubPanel;
                }
            },
            set(subPanel) {
                this.selectedSubPanel = subPanel;
            }
        },

        locales() {
            if (this.subPanelModel) {
                return this.subPanelModel.locales.map(locale => locale.code);
            }
        },

        translationsField() {
            if (this.template === "project-invitation") {
                return "email_translations";
            } else if (this.template === "project-reminder") {
                return "reminder_translations"
            } else {
                throw new Error("Invalid invitation type");
            }
        },

        translations() {

            if (this.data && this.localeModel && this.subPanelModel) {
                // Return default translations
                const library = this.data.find(translation => translation.id === this.subPanelModel.id);
                return library.translations[this.localeModel];
            }
        },

        subPanelTranslations() {
            if (this.data && this.localeModel && this.subPanelModel) {
                const subPanel = this.subPanelTranslationsLibrary.find(subPanel => subPanel.id === this.subPanelModel.id);
                return subPanel.translations[this.localeModel];
            }
        },

        availableSubPanels() {
            if (this.selectedSubPanels.length === 0) {
                return [...this.subPanels];
            } else {
                return this.subPanels.filter(subPanel => this.selectedSubPanels.includes(subPanel.id));
            }
        }
    },

    watch: {
        selectedSubPanels() {
            this.fetchTranslations();
        }
    },

    mounted() {
        this.fetchTranslations();
    },

    methods: {

        ...mapActions('ProjectManager', [
            'sendTestEmail'
        ]),

        ...mapActions('SubPanels', [
            'loadAllEmailTemplates',
            'loadAllReminderTemplates'
        ]),

        emailSubjectChanged(subject) {
            let translations = this.findTranslations();
            translations.subject = subject;
            this.emit();
        },

        senderNameChanged(name) {
            let translations = this.findTranslations();
            translations.sender_name = name;
            this.emit();
        },

        findTranslations() {
            const library = this.data.find(translation => parseInt(translation.id) === parseInt(this.subPanelModel.id));
            return library.translations[this.localeModel];
        },

        mergeTranslations(target) {

            // New project
            if (!this.project[this.translationsField]) {
                return target;
            }

            target.forEach(subPanelTemplate => {
                const projectTemplate = this.project[this.translationsField].find(sp => subPanelTemplate.id === sp.id);

                if (projectTemplate) {
                    subPanelTemplate.translations = merge.all([subPanelTemplate.translations, projectTemplate.translations]);
                }
            });
            return target;
        },

        fetchTranslations() {

            if (this.availableSubPanels.length > 0) {

                this.loading = true;

                const subPanelIds = this.availableSubPanels.map(subPanel => subPanel.id);

                if (this.template === "project-invitation") {

                    this.loadAllEmailTemplates({
                        subPanelIds: subPanelIds,
                        type: this.template
                    })
                        .then((response) => {

                            // Back fill from defaults when needed
                            this.data = this.mergeTranslations(response.data.email_translations);
                            this.subPanelTranslationsLibrary = response.data.sub_panel_translations;

                            this.prepData();
                        })
                        .catch((error) => {
                            console.log(error);
                            Bus.$emit('flash-message', {type: 'error', message: 'Failed loading email templates'});
                        })
                        .finally(() => {
                            this.loading = false;
                        })

                } else if (this.template === 'project-reminder') {

                    this.loadAllReminderTemplates({
                        subPanelIds: subPanelIds,
                        type: this.template
                    })
                        .then((response) => {

                            // Back fill from defaults when needed
                            this.data = this.mergeTranslations(response.data.email_translations);
                            this.subPanelTranslationsLibrary = response.data.sub_panel_translations;

                            this.prepData();
                        })
                        .catch((error) => {
                            console.log(error);
                            Bus.$emit('flash-message', {type: 'error', message: 'Failed loading email templates'});
                        })
                        .finally(() => {
                            this.loading = false;
                        })
                }
            }
        },

        prepData() {

            this.selectedSubPanel = this.availableSubPanels[0];
            this.selectedLocale = this.findPreferredLocale(this.selectedSubPanel).code;

            this.emit();
        },

        subPanelChanged() {
            const locale = this.selectedSubPanel.locales.find(locale => locale.code === this.selectedLocale);
            this.selectedLocale = locale ? locale.code : this.findPreferredLocale(this.selectedSubPanel).code;
        },

        localeChanged(locale) {
            this.showRememberButton = true;
        },

        rememberLocaleButtonClicked() {
            this.showRememberButton = false;
            this.setPreferredLocale(this.localeModel);
        },

        updateEmailTranslations(data) {

            let translation = this.findTranslations();

            translation.body = data.body;
            translation.action_button_text = data.action_button_text;
            translation.signature_block = data.signature_block;

            this.emit();
        },

        sendTestEmailToCurrentUser() {

            if (!this.project.id) {

                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'You need to save the project first before you can send yourself a test email!'
                });
                return;
            }

            this.sending = true;

            const subPanel = this.data.find(subPanel => subPanel.id === this.selectedSubPanel.id);

            let data = {
                subPanelId: this.subPanelModel.id,
                locale: this.localeModel,
                type: this.template,
                translations: subPanel.translations[this.localeModel],
                project: {
                    id: this.project.id ? this.project.id : null,
                    loi: this.project.loi,
                    reward_completed: this.project.reward_completed,
                    mobile_friendly: this.project.mobile_friendly,
                    tablet_friendly: this.project.tablet_friendly,
                    desktop_friendly: this.project.desktop_friendly,
                }
            };

            this.sendTestEmail(data)
                .then((response) => {
                    Bus.$emit('flash-message', {
                        type: 'info',
                        message: 'Invitation was sent to ' + this.$store.state.user.email
                    });

                })
                .catch(() => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'We are sorry! Test email failed to send!'
                    });
                })
                .finally(() => {
                    this.sending = false;
                });
        },

        emit() {
            let object = {};
            object[this.translationsField] = this.data;
            this.$emit('change:translations', Object.assign({}, object));
        }
    }
}
</script>
